import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import getConfig from 'next/config'
import styled from '@emotion/styled'
import gql from 'graphql-tag'
import { withApollo } from '@apollo/react-hoc'
import { captureException } from '~/lib/sentry'
import cookies from 'js-cookie'
import { Alert, Button, color, space } from '@ticketswap/solar'
import { Apple } from '@ticketswap/comets'
import {
  AUTHENTICATION_STARTED,
  AUTHENTICATION_COMPLETED,
} from '../../lib/tracking-events'
import { track, setTrackingIdentity } from '../../utils'
import getViewer from '../../lib/get-viewer'
import { GENERATE_AUTH_TOKEN } from './generateAuthToken'
import { useViewer } from '../ViewerContext'
import { useScript } from '~/hooks/useScript'
import { useMutation } from '@apollo/react-hooks'

const {
  CLIENT_ID,
  TOKEN_COOKIE_NAME,
  BASE_URL,
} = getConfig().publicRuntimeConfig

const AUTHENTICATE_USER_WITH_APPLE = gql`
  mutation authenticateUserWithApple($input: AuthenticateAppleUserInput!) {
    appleAuth: authenticateUserWithApple(input: $input) {
      apiAccessToken {
        tokenString
        expiresAt
      }
      isNew
    }
  }
`

const StyledAlert = styled(Alert)`
  margin-bottom: ${space[8]};
`

const StyledButton = styled(Button)`
  background-color: ${color.space};
  color: white;

  &:focus::after {
    box-shadow: 0 0 0 ${space[4]} ${color.spaceLighterAlpha};
  }

  &:hover {
    background-color: ${color.space};
    background-image: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0.24),
      rgba(255, 255, 255, 0.15)
    );
  }

  &:active {
    background-color: ${color.space};
  }
`

const AppleIDButton = ({ client, onSignup, onLogin, children }) => {
  const [errorMessage, setErrorMessage] = useState(null)
  const [loadingApple, setLoadingApple] = useState(false)
  const { setViewer } = useViewer()
  const [appleAuth, setAppleAuth] = useState(null)
  const [loaded] = useScript(
    'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js'
  )
  const [generate] = useMutation(GENERATE_AUTH_TOKEN)
  const [authenticate] = useMutation(AUTHENTICATE_USER_WITH_APPLE)

  useEffect(() => {
    if (loaded && window.AppleID) {
      window.AppleID.auth.init({
        clientId: 'com.ticketswap.signinwithapplewebservice',
        scope: 'name email',
        redirectURI: BASE_URL,
        usePopup: true,
      })

      setAppleAuth(window.AppleID.auth)
    }
  }, [loaded, appleAuth])

  const handleCompleted = async appleData => {
    const {
      data: { authenticateClientForToken },
    } = await generate({
      variables: {
        input: {
          clientId: CLIENT_ID,
        },
      },
    })

    const input = {
      token: authenticateClientForToken.token,
      appleAccessToken: appleData.authorization.id_token,
    }

    if (appleData.user) {
      input.firstName = appleData.user.name.firstName
      input.lastName = appleData.user.name.lastName
    }

    let data

    try {
      const {
        data: { appleAuth: appleAuthData },
      } = await authenticate({
        variables: { input },
      })
      data = appleAuthData
    } catch (error) {
      return handleError(error)
    }
    const { tokenString } = data.apiAccessToken

    cookies.set(TOKEN_COOKIE_NAME, tokenString, {
      expires: 30,
      secure: true,
    })

    const user = await getViewer(client)
    setTrackingIdentity(user)

    if (data.isNew) {
      return onSignup(appleData.user.name)
    }

    track(AUTHENTICATION_COMPLETED, {
      method: 'apple',
      isNew: data.isNew,
    })

    await client.resetStore()
    setViewer(user)
    onLogin()
  }

  const handleError = ({ error }) => {
    setLoadingApple(false)

    if (error === 'popup_closed_by_user') return

    track(AUTHENTICATION_COMPLETED, {
      method: 'apple',
      error,
    })

    captureException(error, 'appleId')
  }

  return (
    <>
      {errorMessage && (
        <StyledAlert variant="error">{errorMessage}</StyledAlert>
      )}
      <StyledButton
        loading={loadingApple}
        disabled={!loaded}
        width="full"
        leftAdornment={<Apple size={24} />}
        onClick={async () => {
          setErrorMessage(null)
          setLoadingApple(true)
          track(AUTHENTICATION_STARTED, {
            method: 'apple',
          })

          try {
            const data = await appleAuth.signIn()
            handleCompleted(data)
          } catch (error) {
            handleError(error)
          }
        }}
      >
        {children}
      </StyledButton>
    </>
  )
}

AppleIDButton.propTypes = {
  client: PropTypes.object.isRequired,
  onSignup: PropTypes.func.isRequired,
  onLogin: PropTypes.func.isRequired,
}

export default withApollo(AppleIDButton)
